import type { Color } from "@asap/design/styles/colors";
import { colors } from "@asap/design/styles/colors";
import { consultantKpiPldErrorKeys } from "@asap/shared/schemas/team-member-data";

/** Shared tokens for ApexCharts used in the consultant KPI dashboard. */
export const chartTheme = {
  fontFamily: "Inter, sans-serif",
  fontSize: {
    axis: 10,
    label: 11,
  },
  color: {
    ink: colors.secondary[900],
    muted: colors.secondary[800],
    faint: colors.secondary[700],
    grid: colors.secondary[200],
    track: colors.secondary[100],
    success: colors.success[500],
    successSoft: colors.success[200],
    warning: colors.warning[500],
    warningSoft: colors.warning[200],
    critical: colors.critical[500],
    criticalSoft: colors.critical[200],
  },
} as const;

export type ChartStatus = "success" | "warning" | "critical" | "neutral";

/** Shown when a KPI is sourced from PLD and not yet connected. */
export const UNAVAILABLE_KPI_LABEL = "Bientôt disponible";

/** Shown when PLD data could not be synchronized. */
export const PLD_EMPTY_LABEL = "Aucune donnée";

export const PLD_SYNC_ERROR_DESCRIPTION = "Nous n'avons pas réussi à synchroniser les données PLD";

export const PLD_NO_DATA_DESCRIPTION = "Il semble qu'il n'y ait pas de données sur PLD";

export const PLD_NOT_CONFIGURED_DESCRIPTION = "Les données PLD ne sont pas encore disponibles.";

export const PLD_CONSULTANT_NAME_MISSING_DESCRIPTION = "Impossible d'identifier le consultant dans PLD.";

export const resolvePldEmptyDescription = (errorKey: string | null): string => {
  switch (errorKey) {
    case consultantKpiPldErrorKeys.notFound:
      return PLD_NO_DATA_DESCRIPTION;
    case consultantKpiPldErrorKeys.queryFailed:
      return PLD_SYNC_ERROR_DESCRIPTION;
    case consultantKpiPldErrorKeys.notConfigured:
      return PLD_NOT_CONFIGURED_DESCRIPTION;
    case consultantKpiPldErrorKeys.consultantNameMissing:
      return PLD_CONSULTANT_NAME_MISSING_DESCRIPTION;
    default:
      return PLD_SYNC_ERROR_DESCRIPTION;
  }
};

/** Shown when the API returns null for a metric that is otherwise available. */
export const MISSING_KPI_VALUE = "—";

export const achievementPercent = (value: number, target: number): number =>
  target > 0 ? Math.round((value / target) * 100) : 0;

export const statusColor = (status: ChartStatus): string => {
  const map: Record<ChartStatus, string> = {
    success: chartTheme.color.success,
    warning: chartTheme.color.warning,
    critical: chartTheme.color.critical,
    neutral: chartTheme.color.faint,
  };
  return map[status];
};

/** Color name from the design system, usable in UnoCSS classes (covered by the safelist). */
export const statusColorName = (status: ChartStatus): Color => {
  const map: Record<ChartStatus, Color> = {
    success: "green",
    warning: "orange",
    critical: "red",
    neutral: "secondary",
  };
  return map[status];
};

/** Literal classes extracted thanks to `@unocss-include`: gradient utilities are not in the safelist. */
export const statusGradientClass = (status: ChartStatus): string => {
  const map: Record<ChartStatus, string> = {
    success: "bg-gradient-to-b from-green-50/50 to-white",
    warning: "bg-gradient-to-b from-orange-50/50 to-white",
    critical: "bg-gradient-to-b from-red-50/50 to-white",
    neutral: "bg-gradient-to-b from-secondary-50/50 to-white",
  };
  return map[status];
};

export const statusSoftColor = (status: ChartStatus): string => {
  const map: Record<ChartStatus, string> = {
    success: chartTheme.color.successSoft,
    warning: chartTheme.color.warningSoft,
    critical: chartTheme.color.criticalSoft,
    neutral: chartTheme.color.grid,
  };
  return map[status];
};

/** Gradient palette for funnels: the first step is the darkest. */
export const funnelPalette = [colors.success[400], colors.success[300], colors.success[200], colors.success[100]];

const numberFormatter = new Intl.NumberFormat("fr-FR");

export const formatNumber = (value: number, fractionDigits = 0): string =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);

export const formatEuro = (value: number): string => `${numberFormatter.format(Math.round(value))}€`;

export const formatEuroWithDecimals = (value: number, fractionDigits = 1): string =>
  `${formatNumber(value, fractionDigits)}€`;

export const formatCompactEuro = (value: number): string =>
  value >= 1000 ? `${formatNumber(value / 1000, 1)}k€` : formatEuro(value);

export const formatPercent = (value: number, fractionDigits = 0): string => `${formatNumber(value, fractionDigits)}%`;

/** Signed delta as displayed under the KPIs: "+260", "-1,9". */
export const formatDelta = (value: number, fractionDigits = 0): string =>
  `${value > 0 ? "+" : ""}${formatNumber(value, fractionDigits)}`;

/** Signed delta carrying the unit of the KPI it belongs to: "+0,18", "-1,1", "+2,5€". A zero delta is shown as "-". */
export const formatSignedValue = (value: number, formatValue: (value: number) => string): string => {
  if (value === 0) return "-";
  return `${value > 0 ? "+" : "-"}${formatValue(Math.abs(value))}`;
};

export const achievementStatus = (percent: number): ChartStatus => {
  if (percent >= 100) return "success";
  if (percent >= 80) return "warning";
  return "critical";
};

export const deltaStatus = (delta: number): ChartStatus => {
  if (delta > 0) return "success";
  if (delta < 0) return "critical";
  return "neutral";
};
